import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { TaxRate } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { COUNTRIES } from '@/features/markets';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyTaxRateForm, fromTaxRate, toTaxRateBody } from '../api/mappers';
import { useCreateTaxRate, useUpdateTaxRate } from '../hooks/use-tax-rate-mutations';
import {
  taxRateFormSchema,
  type TaxRateFormInput,
  type TaxRateFormOutput,
} from '../schemas/tax-rate-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'name',
  'countryCode',
  'region',
  'postalPattern',
  'ratePercent',
  'appliesToShipping',
  'priority',
  'isActive',
]);
const mapField = (field: string) => (field === 'rate' ? 'ratePercent' : field);

export interface TaxRateDialogProps {
  storeId: string;
  /** null creates; a rate edits it. */
  rate: TaxRate | null;
  defaultCountry: string;
  nextPriority: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TaxRateDialog({
  storeId,
  rate,
  defaultCountry,
  nextPriority,
  open,
  onOpenChange,
}: TaxRateDialogProps) {
  const { t } = useTranslation(['tax', 'common']);
  const create = useCreateTaxRate(storeId);
  const update = useUpdateTaxRate(storeId);
  const message = useErrorMessage();
  const form = useForm<TaxRateFormInput, unknown, TaxRateFormOutput>({
    resolver: useI18nResolver(taxRateFormSchema),
    defaultValues: rate ? fromTaxRate(rate) : emptyTaxRateForm(defaultCountry, nextPriority),
  });
  const isPending = create.isPending || update.isPending;

  const close = () => {
    onOpenChange(false);
    create.reset();
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const body = toTaxRateBody(values);
      if (rate) await update.mutateAsync({ id: rate.id, body });
      else await create.mutateAsync(body);
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{rate ? t('tax:edit.title') : t('tax:create.title')}</DialogTitle>
          <DialogDescription>{t('tax:create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('tax:fields.name')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="countryCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('tax:fields.country')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('tax:fields.countryPlaceholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('tax:fields.region')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" className="uppercase" {...field} />
                    </FormControl>
                    <FormDescription>{t('tax:fields.regionHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="postalPattern"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('tax:fields.postalPattern')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" className="font-mono" {...field} />
                    </FormControl>
                    <FormDescription>{t('tax:fields.postalPatternHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ratePercent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('tax:fields.rate')}</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input inputMode="decimal" autoComplete="off" className="pr-8" {...field} />
                      </FormControl>
                      <span
                        className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm"
                        aria-hidden
                      >
                        %
                      </span>
                    </div>
                    <FormDescription>{t('tax:fields.rateHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('tax:fields.priority')}</FormLabel>
                    <FormControl>
                      <Input type="number" inputMode="numeric" min={0} step={1} {...field} />
                    </FormControl>
                    <FormDescription>{t('tax:fields.priorityHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-col gap-3">
                <FormField
                  control={form.control}
                  name="appliesToShipping"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                      <FormLabel>{t('tax:fields.appliesToShipping')}</FormLabel>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                      <FormLabel>{t('tax:fields.isActive')}</FormLabel>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? t('common:actions.saving')
                  : rate
                    ? t('common:actions.save')
                    : t('tax:create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
